/**
 * The one look of a text-like control: `Field`, `TextAreaField`, `SelectField`, `Select` and
 * `SearchField` all draw their box from here, so a field in a phone form, a filter above a table
 * and a select in a table cell are the same thing at two sizes.
 *
 * - `regular`: 52px high, for forms on a phone and settings pages, where a thumb types.
 * - `compact`: 40px high, for tables, toolbars and the sidebar, where many controls share a line.
 *
 * Every size shares the border, the focus ring (only for the keyboard, `focus-visible`), the red
 * border of an error and the sunken, greyed look of a disabled control. Nothing else draws an input
 * (AGENTS.md, "Inputs").
 */

export type ControlSize = "regular" | "compact";

const BASE =
  "sk-field w-full min-w-0 border text-ink placeholder:text-ink-3 transition-colors duration-150 focus:outline-none focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-accent-soft disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-3 disabled:shadow-none";

/** Height, corner and type size per size. Padding stays with the control, which knows its icons. */
export const CONTROL_SIZES: Record<ControlSize, string> = {
  regular: "h-13 rounded-lg text-base",
  compact: "h-10 rounded-lg text-[14px]",
};

/** The border: red for an error, else the line that turns blue while the control has focus. */
export function controlBorder(error: boolean): string {
  return error ? "border-cancelled" : "border-line-strong focus:border-accent-soft";
}

export function controlClass({
  size = "regular",
  error = false,
  className = "",
  sized = true,
}: {
  size?: ControlSize;
  error?: boolean;
  className?: string;
  /** False for a text area, which sets its own height and keeps only the regular corner and type. */
  sized?: boolean;
}): string {
  return [BASE, sized ? CONTROL_SIZES[size] : "rounded-lg text-base", controlBorder(error), className]
    .filter(Boolean)
    .join(" ");
}

/** The id of the hint or error line under a control, for `aria-describedby`. */
export function hintId(id: string): string {
  return `${id}-hint`;
}
